import {
  highIncomeCountries,
  lowLowerMiddleIncomeCountries,
  upperMidIncomeCountries,
} from '@/assets/messages';
import { AnnotatedType, PrimaryAnnotatedType } from '@/components/annotated-type';
import { HorizontalBarChart } from '@/components/bar-chart/horizontal';
import { ScrollGraphic, ScrollRoot, ScrollStep, ScrollSteps } from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useState } from 'react';

type ChildPowerChapter2Props = {
  dataChildPopulation: ChildPowerData['child_population'];
  dataListenToChildren: ChildPowerData['importance_to_listen_to_children_income_bd'];
};

export function ChildPowerChapter2(props: ChildPowerChapter2Props) {
  const { dataChildPopulation, dataListenToChildren } = props;

  const { i18n } = useLingui();
  const formatValue = useNumberFormatting('percent');
  const [chartVisible, setChartVisible] = useState(false);

  return (
    <ScrollRoot>
      <ScrollGraphic>
        <HorizontalBarChart
          label={t({
            id: 'child-power.chapter-2.chart-label',
            message:
              '% of 40+ year-olds who believe it’s very important that political leaders listen to children when making decisions',
          })}
          visible={chartVisible}
          formatValue={formatValue}
          data={[
            { label: highIncomeCountries(i18n), value: dataListenToChildren.hic },
            { label: upperMidIncomeCountries(i18n), value: dataListenToChildren.umic },
            { label: lowLowerMiddleIncomeCountries(i18n), value: dataListenToChildren.lmic },
          ]}
        />
      </ScrollGraphic>

      <ScrollSteps onStepEnter={() => setChartVisible(true)}>
        <ScrollStep>
          <StoryText>
            <Trans id="child-power.chapter-2.text-1">
              <AnnotatedType>Developing countries</AnnotatedType> have the biggest majorities
              expressing support for political leaders listening to children.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep>
          <StoryText>
            <Trans id="child-power.chapter-2.text-2">
              It makes particularly good sense to listen to children’s voices in the developing
              world, where children make up <AnnotatedType>a larger share</AnnotatedType> of the
              population.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep>
          <StoryText>
            <Trans id="child-power.chapter-2.text-3">
              In low- and lower-middle income countries,{' '}
              <AnnotatedType>{formatValue(dataChildPopulation.lic)}</AnnotatedType> of the
              population are children. On average,{' '}
              <PrimaryAnnotatedType>{formatValue(dataListenToChildren.lmic)}</PrimaryAnnotatedType>{' '}
              of older people say it is very important that politicians listen to children in these
              countries.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep>
          <StoryText>
            <Trans id="child-power.chapter-2.text-4">
              By contrast, in high-income countries, only{' '}
              <AnnotatedType>{formatValue(dataChildPopulation.hic)}</AnnotatedType> of the
              population are children. On average,{' '}
              <PrimaryAnnotatedType>{formatValue(dataListenToChildren.hic)}</PrimaryAnnotatedType>{' '}
              of older people in these countries say it is very important that politicians listen to
              children.
            </Trans>
          </StoryText>
        </ScrollStep>
      </ScrollSteps>
    </ScrollRoot>
  );
}
